import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from "react-router-dom";
import Graph from '../engine/GraphCanvas';
import constants from '../helpers/constants';
import Navbar from './Navbar';
import { useParams } from 'react-router-dom';
import usePdfProcessor from '../hooks/usePdfProcessor';
import { useNotification } from 'rc-notification';
import NotificationContainer from './NotificationContainer';
import Modal from './Modal';
import TaskQueue from '../components/TaskQueue';
import { useCloseModalListener } from '../hooks/useCloseModalListener';
import { useScreenResizing } from '../hooks/useScreenResizing';
import { useHandleGraphUpdates } from '../hooks/useHandleGraphUpdates';
import useHandleNewGameState from '../hooks/useHandleNewGameState';
import { useTranslation } from 'react-i18next';
import Chat from './Chat';
import LoginModal from './LoginModal';
import SettingsModal from './SettingsModal';
import { addOrUpdateGraph, deleteByKey, getFromIndexedDB, StorageLimitError } from '../helpers/indexedDb';
import { generateRandomKey } from '../helpers/generateRandomKey';
import { IoCloseCircle } from 'react-icons/io5';
import { motion } from '../helpers/motion';
import { deleteFileFromServer } from '../helpers/deleteGraphFromServer';
import { Menu, Item, useContextMenu } from "react-contexify";
import "../style/Graph.css";
import "../style/ReactContexify.css";
import { getNodeContent } from '../helpers/getNodeContent';
import NodeContent from './NodeContent';
import EdgeEntity from '../entities/EdgeEntity';
import addEdgeAndUpdateGraph from '../helpers/addEdgeAndUpdateGraph';
import buildGraph from '../helpers/buildGraph';
import getOrSetGraph from '../helpers/getOrSetGraph';
import improveGraph from '../helpers/improveGraph';
import SubmenuSearchAbout from './SubmenuSearchAbout';
import FlashcardModal from './FlashcardModal';
import getNeighborsGraphById from '../helpers/getNeighborsGraphById';
import { notifyAndInviteToSubscribe } from './notifyAndInviteToSubscribe';
import getApiHeaders from '../helpers/apiHeaders';
import fitNodesOnScreen from '../helpers/fitNodesOnScreen';
import getRandomNode from '../helpers/getRandomNode';
import getNodeByBestMatch from './getNodeByBestMatch';
import focusOnNode from '../helpers/focusOnNode';
import { updateNodeInGraph } from '../helpers/updateNodeInGraph';
import * as pdfjsLib from 'pdfjs-dist';
import NodeListSidebar from './NodeListSidebar';
import findParents from '../helpers/findParents';
import GraphLegend from './GraphLegend';
import type { Graph as GraphType, GameState } from '../types';

// Set up the worker for pdfjs
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.mjs`;

const taskQueue = new TaskQueue();
taskQueue.start();

const GameGraph: React.FC = () => {
  const { t } = useTranslation();
  const graphRef = useRef<HTMLDivElement>(null);
  const networkRef = useRef<any>(null);
  const pulseNodeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const answerNotificationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const useHandleGraphUpdatesTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const buildGraphRef = useRef<boolean>(false);
  const [rebuildCounter, setRebuildCounter] = useState(0);

  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const { topic, fileId } = params;

  // Detect if we're in file mode or topic mode
  const isFileMode = location.pathname.startsWith('/file/');
  
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [screenHeight, setScreenHeight] = useState(window.innerHeight);
  const [physicsEnabled] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [embeddingProgress, setEmbeddingProgress] = useState<string | null>(null);
  const streamTextRef = useRef('');
  const streamOpacityRef = useRef(0);
  const streamFadeTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  
  // PDF processing for file mode
  const { processAllPages, deletePdfFile } = usePdfProcessor();
  const processAllPagesRef = useRef(processAllPages);
  processAllPagesRef.current = processAllPages;
  const isProcessingRef = useRef(false);
  const [, setGameState] = useState<GameState>({});
  const [newGameState, setNewGameState] = useState<GameState>({});
  const [, setHover] = useState<boolean | null>(null);
  const [, setEdgeHover] = useState(false);
  const [lastHoveredNode, setLastHoveredNode] = useState<any>(false);
  const [, setEdgeSelected] = useState<any>(null);

  const [lastSelectedNode, setLastSelectedNode] = useState<any>({});
  const [lastSelectedNodeParents, setLastSelectedNodeParents] = useState<any[]>([]);
  const [showChat, setShowNodeContent] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [flashcardNode, setFlashcardNode] = useState<any>(null);
  const [flashcardNeighbors, setFlashcardNeighbors] = useState<any>(null);
  const [showChatAboutContextButton, setShowChatAboutContextButton] = useState(false);
  const [addingNewEdgeFrom] = useState(false);
  const [addingNewEdge, setAddingNewEdge] = useState(false);
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [newPrompt, setNewPrompt] = useState('');
  const [isNodeListOpen, setIsNodeListOpen] = useState(() => window.innerWidth > 768);
  
  // Track if chat has been auto-opened to prevent multiple auto-opens
  const chatAutoOpenedRef = useRef<boolean>(false);

  const notificationConfig = useMemo(() => ({
    showProgress: true,
    getContainer: () => document.getElementById('notification-container')!,
    motion,
  }), []);
  const [notice, contextHolder] = useNotification(notificationConfig);

  const saveGraph = useCallback(async (graph: any, key: string | undefined) => {
    if (!key) return;
    try {
      await addOrUpdateGraph(graph, key);
    } catch (err) {
      if (err instanceof StorageLimitError) {
        const noticeKey = generateRandomKey();
        notice.open({
          key: noticeKey,
          duration: 10,
          content: (
            <div>
              <div className="d-flex justify-content-between align-items-center mr-1">
                <small className="tag">{t('common.ops')}</small>
                <strong>{t('notifications.storageFull')}</strong>
              </div>
              <div>{t('notifications.storageFullDetail', { used: err.usedMB, limit: err.limitMB })}</div>
            </div>
          ),
          closable: true,
          showProgress: false,
          closeIcon: <IoCloseCircle />,
        });
      }
      console.error('[Storage]', err);
    }
  }, [notice, t]);

  const [graph, setGraph] = useState<GraphType>({
    nodes: [
      {
        id: -1,
        label: t('common.loading'),
        title: t('common.loading'),
        group: "loading",
      }
    ],
    edges: []
  });

  // Track which nodes have their children expanded (visible)
  const [expandedNodes, setExpandedNodes] = useState<Set<number | string>>(new Set());

  // Expand all nodes in a graph by default
  const expandAllNodes = useCallback((graphData: any) => {
    if (graphData?.nodes) {
      setExpandedNodes(new Set(graphData.nodes.map((n: any) => n.id)));
    }
  }, []);

  // Compute the visible subset of the graph: hide children of collapsed parent nodes
  const visibleGraph = useMemo(() => {
    if (!graph?.nodes) return graph;

    // Build a map: parentNodeId → [childNodeIds] for every node that has outgoing edges
    const parentChildMap = new Map();
    const nodeMap = new Map(graph.nodes.map(n => [n.id, n]));

    graph.nodes.forEach(node => {
      const childIds = graph.edges
        .filter(e => e.from === node.id)
        .map(e => e.to)
        .filter(id => nodeMap.has(id));
      if (childIds.length > 0) {
        parentChildMap.set(node.id, childIds);
      }
    });

    // Build reverse map: childId → [parentIds that could hide it]
    const childParentsMap = new Map();
    parentChildMap.forEach((childIds: any[], parentId: number | string) => {
      childIds.forEach((childId: number | string) => {
        if (!childParentsMap.has(childId)) {
          childParentsMap.set(childId, []);
        }
        childParentsMap.get(childId).push(parentId);
      });
    });

    const effectiveExpanded = new Set([...expandedNodes]);

    // A node is hidden if ALL of its parents are collapsed OR hidden (recursive).
    // Nodes with no parents (roots) are always visible.
    const hiddenIds = new Set();
    const checkedIds = new Map(); // cache: nodeId → isHidden

    const isHidden = (nodeId: number | string): boolean => {
      if (checkedIds.has(nodeId)) return checkedIds.get(nodeId);
      const parentIds = childParentsMap.get(nodeId);
      if (!parentIds) {
        // Root node (no parents) — always visible
        checkedIds.set(nodeId, false);
        return false;
      }
      // Guard against cycles: assume visible while checking parents
      checkedIds.set(nodeId, false);
      // Hidden if ALL parents are either collapsed or themselves hidden
      const hidden = parentIds.every((pid: number | string) => !effectiveExpanded.has(pid) || isHidden(pid));
      checkedIds.set(nodeId, hidden);
      return hidden;
    };

    childParentsMap.forEach((_, childId) => {
      if (isHidden(childId)) {
        hiddenIds.add(childId);
      }
    });

    if (hiddenIds.size === 0 && parentChildMap.size === 0) return graph;

    const visibleNodes = graph.nodes
      .filter(n => !hiddenIds.has(n.id));
    const visibleEdges = graph.edges.filter(e => !hiddenIds.has(e.from) && !hiddenIds.has(e.to));

    return { ...graph, nodes: visibleNodes, edges: visibleEdges };
  }, [graph, expandedNodes]);

  useHandleNewGameState({
    newGameState,
    setNewGameState,
    setGameState,
    networkRef,
    notice,
    pulseNodeTimerRef,
    answerNotificationTimerRef,
    taskQueue,
    topic: topic || ''
  });

useEffect(() => {
    if (networkRef.current) {
      networkRef.current.setOptions({
        physics: { enabled: physicsEnabled },
      });
    }
  }, [physicsEnabled]);

  // Disable physics after stabilization to save CPU.
  // Drag is handled directly by the engine (tickOnce per pointermove), no need to re-enable physics.
  useEffect(() => {
    if (!networkRef.current) return;

    const onStabilized = () => {
      // While pages are still being processed, keep physics running
      // so nodes continuously rearrange as new pages merge in
      if (physicsEnabled && !isProcessingRef.current) {
        networkRef.current.setOptions({ physics: { enabled: false } });
      }
    };

    networkRef.current.on('stabilized', onStabilized);

    return () => {
      if (networkRef.current) {
        networkRef.current.off('stabilized', onStabilized);
      }
    };
  }, [physicsEnabled]);

  // After initial layout, keep running smooth per-frame physics for 5s
  // so nodes settle nicely, with the camera smoothly tracking them.
  // Physics runs for the full 5s. Camera lerp stops on user interaction.
  useEffect(() => {
    if (!networkRef.current) return;
    const engine = networkRef.current;
    const camera = engine._camera;
    const canvas = engine._canvas;
    const start = Date.now();
    let rafId: number;
    let userTookControl = false;

    const onUserInteraction = () => { userTookControl = true; };
    canvas.addEventListener('pointerdown', onUserInteraction, { once: true });
    canvas.addEventListener('wheel', onUserInteraction, { once: true });

    const tick = () => {
      const elapsed = Date.now() - start;
      if (!engine._physics || elapsed > 5000) return;

      for (let i = 0; i < 3; i++) engine._physics.tickOnce();

      if (!userTookControl) {
        // Smoothly lerp camera toward fit target
        camera._stopAnimation();
        const prevX = camera.x, prevY = camera.y, prevScale = camera.scale;
        engine.fit();
        const t = 0.08;
        camera.x = prevX + (camera.x - prevX) * t;
        camera.y = prevY + (camera.y - prevY) * t;
        camera.scale = prevScale + (camera.scale - prevScale) * t;
      }

      engine.markDirty();
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(rafId);
      canvas.removeEventListener('pointerdown', onUserInteraction);
      canvas.removeEventListener('wheel', onUserInteraction);
    };
  }, []);

  // Animate physics when the graph changes (new nodes/edges from page merges).
  // Uses requestAnimationFrame + tickOnce for smooth motion instead of the
  // synchronous _layoutSync burst that teleports nodes to final positions.
  useEffect(() => {
    if (!networkRef.current || !physicsEnabled) return;

    const engine = networkRef.current;
    if (!engine._physics) return;

    // Sync the physics data so new nodes are included
    engine._physics.syncFromDataSet();

    const start = Date.now();
    let rafId: number;

    const camera = engine._camera;

    const tick = () => {
      if (!engine._physics) return;

      // During processing, run indefinitely (new pages will keep arriving).
      // Otherwise, run for 3 seconds to let nodes settle after a change.
      const elapsed = Date.now() - start;
      if (!isProcessingRef.current && elapsed > 3000) return;

      for (let i = 0; i < 3; i++) engine._physics.tickOnce();

      // Smoothly lerp camera to fit all nodes so the view zooms out as nodes are added
      if (camera) {
        camera._stopAnimation();
        const prevX = camera.x, prevY = camera.y, prevScale = camera.scale;
        engine.fit();
        const t = 0.08;
        camera.x = prevX + (camera.x - prevX) * t;
        camera.y = prevY + (camera.y - prevY) * t;
        camera.scale = prevScale + (camera.scale - prevScale) * t;
      }

      engine.markDirty();
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);

    return () => { cancelAnimationFrame(rafId); };
  }, [visibleGraph?.nodes?.length, physicsEnabled]);

  // Switch to barnesHut solver (O(n log n)) when visible node count exceeds 100
  const barnesHutAppliedRef = useRef(false);
  useEffect(() => {
    if (barnesHutAppliedRef.current) return;
    if (!networkRef.current || !visibleGraph?.nodes) return;
    const nodeCount = visibleGraph.nodes.length;
    if (nodeCount > 100) {
      barnesHutAppliedRef.current = true;
      networkRef.current.setOptions({
        physics: {
          solver: 'barnesHut',
          barnesHut: {
            gravitationalConstant: -1500,
            centralGravity: 0.05,
            springLength: 350,
            springConstant: 0.008,
            damping: 0.4,
          },
        },
      });
    }
  }, [visibleGraph?.nodes, networkRef]);

  const handleRemoveGraph = async () => {
    const key = isFileMode ? fileId : topic;
    if (!key) return;
    const confirmed = window.confirm(t('navbar.confirmDeleteGraph'));
    if (!confirmed) return;
    try {
      await deleteByKey(key);
    } catch (error) {
      console.error("Error removing graph from IndexedDB:", error);
    }
    // Remove from localStorage history
    try {
      const history = JSON.parse(localStorage.getItem("lastClickedTopics") || "[]");
      const updated = history.filter((t: any) => t.name !== key);
      localStorage.setItem("lastClickedTopics", JSON.stringify(updated));
    } catch {}
    if (isFileMode) {
      try {
        await deletePdfFile(key);
      } catch (error) {
        console.error("Error removing PDF from IndexedDB:", error);
      }
      try {
        await deleteFileFromServer(key);
      } catch (error) {
        console.error("Error removing file from server:", error);
      }
    }
    navigate("/");
  }

  const handleRebuildGraph = async () => {
    const key = isFileMode ? fileId : topic;
    if (!key) return;
    const confirmed = window.confirm(t('navbar.confirmRebuildGraph'));
    if (!confirmed) return;
    try {
      await deleteByKey(key);
    } catch (error) {
      console.error("Error removing graph from IndexedDB:", error);
    }
    // Reset state to trigger re-processing
    setGraph(null);
    streamTextRef.current = '';
    streamOpacityRef.current = 0;
    isProcessingRef.current = false;
    buildGraphRef.current = false;
    setRebuildCounter(c => c + 1);
  }

  useHandleGraphUpdates(visibleGraph, networkRef, useHandleGraphUpdatesTimerRef);
  useScreenResizing(setScreenWidth, setScreenHeight);
  useCloseModalListener([setShowLoginModal, setShowNodeContent, setShowEdit, setShowSettingsModal, () => { setFlashcardNode(null); }]);


  const events = {
    selectNode: (event: any) => {
      setEdgeSelected(null);

      const { nodes } = event;
      const selectedNode = getNodeContent(graph, nodes[0]);
      if (!selectedNode) return;
      setLastSelectedNode(selectedNode);

      // Find parent nodes for the selected node
      const parentNodes = findParents(graph, selectedNode.id).filter(node => node !== undefined);
      setLastSelectedNodeParents(parentNodes);

      if (addingNewEdge) {
        if (selectedNode.id !== (addingNewEdgeFrom as any)) {
          const edge = new EdgeEntity(addingNewEdgeFrom as any, selectedNode.id)
          addEdgeAndUpdateGraph(edge, graph, topic!, setGraph)
        }

        setAddingNewEdge(false)
        return;
      }
    },
    hoverNode: (event: any) => {
      if (showContextMenu === false) {
        setLastHoveredNode(event.node)
      }

      setHover(true)
      document.body.style.cursor = 'pointer';
    },
    blurNode: () => {
      if (!addingNewEdge) {
        document.body.style.cursor = 'default';
      }

      setHover(false)
    },
    selectEdge: (event: any) => {
      setEdgeSelected(event.edges[0])
    },
    hoverEdge: () => {
      setEdgeHover(true)
    },
    blurEdge: () => {
      setEdgeHover(false)
    },
    click: (event: any) => {
      if (addingNewEdge) return;
      const hasNode = event.nodes && event.nodes.length > 0;
      const hasEdge = event.edges && event.edges.length > 0;
      if (hasNode || hasEdge) {
        if (hasNode) {
          setLastHoveredNode(event.nodes[0]);
        }
        show({ event: event.event });
        setShowContextMenu(true);
        setShowChatAboutContextButton(hasNode);
      } else {
        setShowContextMenu(false);
        hideAll();
      }
    },
    beforeDrawing: (ctx: any) => {
      const canvasWidth = ctx.canvas.width;
      const canvasHeight = ctx.canvas.height;

      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.fillStyle = constants.graph.options.nodes.color.background;
      ctx.fillRect(0, 0, canvasWidth, canvasHeight);

      // Draw raw LLM stream behind graph nodes
      if (streamOpacityRef.current > 0 && streamTextRef.current) {
        ctx.font = '18px monospace';
        ctx.fillStyle = `rgba(100, 100, 100, ${streamOpacityRef.current})`;
        const maxWidth = 500;
        const lineHeight = 24;
        const rawLines = streamTextRef.current.split('\n');
        // Word-wrap lines to fit maxWidth
        const wrapped: string[] = [];
        for (const line of rawLines) {
          const words = line.split(' ');
          let current = '';
          for (const word of words) {
            const test = current ? current + ' ' + word : word;
            if (ctx.measureText(test).width > maxWidth && current) {
              wrapped.push(current);
              current = word;
            } else {
              current = test;
            }
          }
          if (current) wrapped.push(current);
        }
        const maxLines = Math.floor((canvasHeight * 0.5) / lineHeight);
        const visible = wrapped.slice(-maxLines);
        for (let i = 0; i < visible.length; i++) {
          ctx.fillText(visible[i], 20, 30 + i * lineHeight);
        }
      }

      ctx.restore();
    }
  };

  const menuId = "canvas-rightclick"

  const { show, hideAll } = useContextMenu({
    id: menuId,
  });

  const searchQueryCacheRef = useRef<{ nodeId: any; query: string | null; promise: Promise<string> | null }>({ nodeId: null, query: null, promise: null });

  const getSearchQuery = useCallback(async () => {
    if (!lastHoveredNode || !graph) return '';

    const node = graph.nodes.find((n: any) => n.id === lastHoveredNode);
    if (!node) return '';

    const cache = searchQueryCacheRef.current;

    if (cache.nodeId === node.id) {
      if (cache.query) return cache.query;
      if (cache.promise) return cache.promise;
    }

    const parentNodes = findParents(graph, node.id).filter((n: any) => n);
    const parentLabels = parentNodes.map((n: any) => n.label);

    const promise = fetch(constants.fetchSearchQuery, {
      method: 'POST',
      headers: getApiHeaders(),
      credentials: 'include',
      body: JSON.stringify({
        nodeLabel: node.label,
        parentLabels,
      }),
    })
      .then(res => {
        if (!res.ok) throw new Error('API error');
        return res.json();
      })
      .then(({ query }) => {
        searchQueryCacheRef.current = { nodeId: node.id, query, promise: null };
        return query;
      })
      .catch((err) => {
        console.error('Failed to fetch search query:', err);
        searchQueryCacheRef.current = { nodeId: node.id, query: null, promise: null };
        return '';
      });

    searchQueryCacheRef.current = { nodeId: node.id, query: null, promise };
    return promise;
  }, [graph, lastHoveredNode]);

  const zoomedInRef = useRef(false);

  const handleDoubleClick = (e: any) => {
    if (!networkRef.current) return;

    if (zoomedInRef.current) {
      // Zoom out: fit all nodes
      fitNodesOnScreen(networkRef);
      zoomedInRef.current = false;
    } else {
      // Zoom in: focus on the tapped position at scale 1
      const pointer = networkRef.current.DOMtoCanvas({
        x: e.clientX || e.pointer?.DOM?.x || 0,
        y: e.clientY || e.pointer?.DOM?.y || 0,
      });
      networkRef.current.moveTo({
        position: pointer,
        scale: 1,
        animation: {
          duration: 1000,
          easingFunction: 'easeInOutQuad',
        },
      });
      zoomedInRef.current = true;
    }
  }

  useEffect(() => {
    const hasGraphChildren = document.querySelector('body .graph') !== null;

    if (hasGraphChildren) {
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    if (isFetching) {
      return
    }

    if (!topic) {
      return
    }

    if (!networkRef.current) {
      return
    }

    const topicNode = getNodeByBestMatch(topic, networkRef.current.body.data.nodes.get())
    if (topicNode) {
      // Card game functionality removed
    }
  }, [isFetching, topic, networkRef])

  // Auto-select main node in file mode (only once)
  useEffect(() => {
    if (!isFileMode || !graph || !graph.nodes || chatAutoOpenedRef.current) return;

    const firstNode = graph.nodes[0];
    if (firstNode && !chatAutoOpenedRef.current) {
      setLastSelectedNode(firstNode);
      chatAutoOpenedRef.current = true;
    }
  }, [graph, isFileMode]);


  // Auto-process pages one by one, merging each into a unified graph
  useEffect(() => {
    if (!isFileMode || !fileId) return;
    if (isProcessingRef.current) return;

    let cancelled = false;

    const processPages = async () => {
      try {
        // Check if unified graph is already cached for this file
        const cachedGraph = await getFromIndexedDB(fileId);
        if (cancelled) return;
        if (cachedGraph && cachedGraph.nodes?.length > 1) {
          setGraph({ ...cachedGraph, topic: fileId });
          expandAllNodes(cachedGraph);
          return;
        }

        isProcessingRef.current = true;

        let unifiedGraph: GraphType = { nodes: [], edges: [], topic: fileId };

        await processAllPagesRef.current(fileId!,
          // onPageProcessed — final authoritative graph with colors
          (pageIndex, totalPages, result) => {
            if (cancelled) return;

            if (result?.graph) {
              unifiedGraph = { ...result.graph, topic: fileId };
              if (result.legend) unifiedGraph.legend = result.legend;

              setGraph({ ...unifiedGraph });
              expandAllNodes(unifiedGraph);
              saveGraph(unifiedGraph, fileId!);
            }
          },
          // onStreamChunk — progressive rendering during streaming
          (partialGraph) => {
            if (cancelled) return;
            const streamGraph = { ...partialGraph, topic: fileId };
            setGraph(streamGraph);
            expandAllNodes(streamGraph);
          },
          // onLog — raw LLM tokens drawn on canvas
          (token) => {
            streamTextRef.current += token;
            streamOpacityRef.current = 0.5;
            if (streamFadeTimer.current) clearInterval(streamFadeTimer.current);
            streamFadeTimer.current = setInterval(() => {
              streamOpacityRef.current -= 0.01;
              if (streamOpacityRef.current <= 0) {
                streamOpacityRef.current = 0;
                clearInterval(streamFadeTimer.current!);
                streamFadeTimer.current = null;
              }
              networkRef.current?.markDirty?.();
            }, 50);
            networkRef.current?.markDirty?.();
          },
          // onEmbeddingProgress — SSE progress from /api/file/embed
          (event) => {
            if (event.step === 'embedding') {
              setEmbeddingProgress(`Indexing ${event.current}/${event.total}`);
            } else if (event.step === 'storing') {
              setEmbeddingProgress('Saving...');
            } else if (event.step === 'done') {
              setEmbeddingProgress(null);
            }
          },
        );
      } catch (error: any) {
        if (cancelled) return;
        console.error('[GameGraph] Error processing file pages:', error);
        if (error?.status === 429) {
          notifyAndInviteToSubscribe(notice, error, setShowLoginModal);
        }
      } finally {
        isProcessingRef.current = false;
      }
    };

    processPages();

    return () => { cancelled = true; };
  }, [isFileMode, fileId, notice, setShowLoginModal, expandAllNodes, saveGraph, rebuildCounter]);

  // Reset buildGraphRef when file or topic changes so fetchGraph runs
  useEffect(() => {
    if (isFileMode && fileId) {
      buildGraphRef.current = false;
    }
  }, [isFileMode, fileId]);

  useEffect(() => {
    if (!networkRef.current) {
      return
    }

    if (buildGraphRef.current) {
      return
    }

    buildGraphRef.current = true

    const fetchGraph = async () => {
      // Handle file mode: one unified graph per file
      if (isFileMode && fileId) {
        setIsFetching(true);

        // Try to load cached file graph
        const savedGraph = await getFromIndexedDB(fileId);
        if (savedGraph) {
          savedGraph.topic = fileId;
          setGraph(savedGraph);
          expandAllNodes(savedGraph);
          setIsFetching(false);

          const firstNode = savedGraph.nodes[0];
          if (firstNode && !chatAutoOpenedRef.current) {
            setLastSelectedNode(firstNode);
            chatAutoOpenedRef.current = true;
          }

          return;
        }

        // No cached graph yet — show loading placeholder until processing completes
        const initialGraph = {
          nodes: [{ id: -1, label: t('common.loading'), title: t('common.loading'), group: 'loading' }],
          edges: [],
          topic: fileId,
        };
        setGraph(initialGraph);
        setIsFetching(false);
        return;
      }

      // Handle topic mode (existing logic)
      if (!topic) return;

      setIsFetching(true)

      const graph = await getOrSetGraph(topic)
      if (graph) {
        graph.topic = topic
        setGraph(graph)
        expandAllNodes(graph);
        saveGraph(graph, topic);
        setIsFetching(false)

        // Find main node and auto-open chat (only once on init)
        const firstNode = getNodeByBestMatch(topic, graph.nodes) || graph.nodes[0];
        if (firstNode && !chatAutoOpenedRef.current) {
          setLastSelectedNode(firstNode);
          chatAutoOpenedRef.current = true;
        }
        return
      }

      try {
        const response = await buildGraph({ input: topic })
        response.graph.topic = topic
        if (response.legend) response.graph.legend = response.legend;

        setGraph(response.graph)
        expandAllNodes(response.graph);
        saveGraph(response.graph, topic);

        const bestNode = getNodeByBestMatch(topic!, response.graph.nodes) || response.graph.nodes[0];
        if (bestNode && !chatAutoOpenedRef.current) {
          setLastSelectedNode(bestNode);
          chatAutoOpenedRef.current = true;
        }
        
      } catch (error: any) {
        console.error(error)
        if (error.status && error.status === 429) {
          let message = await error.json()
          notifyAndInviteToSubscribe(notice, { message }, setShowLoginModal)
        }

        const defaultGraph: GraphType = {
          nodes: [{ id: 1, label: topic, title: topic }],
          edges: []
        }

        setGraph(defaultGraph)
        expandAllNodes(defaultGraph);

        // Set default main node as selected (auto-open chat removed)
        const defaultMainNode = defaultGraph.nodes[0];
        if (defaultMainNode && !chatAutoOpenedRef.current) {
          setLastSelectedNode(defaultMainNode);
          chatAutoOpenedRef.current = true;
        }
      } finally {
        setIsFetching(false)
      }
    }

    fetchGraph()

  }, [topic, isFileMode, fileId, notice, setShowLoginModal, expandAllNodes, t, saveGraph, rebuildCounter])

  const sendNewPrompt = async (event: any) => {
    event.preventDefault()
    setIsFetching(true)

    try {
      const response = await improveGraph({ input: newPrompt, graph: graph })

      setGraph(response.graph)
      expandAllNodes(response.graph);
      // Use fileId in file mode, topic in topic mode
      const saveKey = isFileMode ? fileId : topic;
      saveGraph(response.graph, saveKey);
    } catch (error: any) {
      console.error(error)
      if (error.status && error.status === 429) {
        let message = await error.json()
        notifyAndInviteToSubscribe(notice, { message }, setShowLoginModal)
      }
    } finally {
      setIsFetching(false)
      setNewPrompt("")
    }
  }

  const handleBuildGraphInNewTab = () => {
    if (!lastSelectedNode || !lastSelectedNode.label) return;
    
    // Encode the node label for URL
    const encodedLabel = encodeURIComponent(lastSelectedNode.label);
    const newTabUrl = `/${encodedLabel}`;
    
    // Open in new tab
    window.open(newTabUrl, '_blank');
    
    // Close context menu
    setShowContextMenu(false);
    hideAll();
  };

  const handleRandomNodeSelection = useCallback(() => {
    if (!networkRef.current || isFetching) return;

    const randomNode = getRandomNode(networkRef);
    if (!randomNode) return;

    // Focus and zoom in on the random node
    focusOnNode(randomNode.id, networkRef, 0, 0, 1000, 1);
  }, [isFetching]);

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (
        e.key?.toLowerCase() === 'r' &&
        !e.ctrlKey && !e.metaKey && !e.altKey &&
        document.activeElement?.tagName !== 'INPUT' &&
        document.activeElement?.tagName !== 'TEXTAREA' &&
        !(document.activeElement as HTMLElement)?.isContentEditable
      ) {
        handleRandomNodeSelection();
      }
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [handleRandomNodeSelection]);

  const handleZoomOut = () => {
    if (!networkRef.current) return;
    fitNodesOnScreen(networkRef);
  };

  const handleChatIdCreated = useCallback((nodeId: number | string, chatId: string) => {
    setGraph(prevGraph => {
      const updatedGraph = updateNodeInGraph(prevGraph, nodeId, { chatId });
      const saveKey = isFileMode ? fileId : topic;
      saveGraph(updatedGraph, saveKey);
      return updatedGraph;
    });

    // Update lastSelectedNode if it matches the nodeId
    setLastSelectedNode((prevNode: any) => {
      if (prevNode.id === nodeId) {
        return { ...prevNode, chatId };
      }
      return prevNode;
    });
  }, [isFileMode, fileId, topic, saveGraph]);

  return (
    <div className='graph'>
      <div className='absolute z-1 center-y center-x'>
      </div>

      <Navbar
        isFetching={isFetching}
        embeddingProgress={embeddingProgress}
        setShowLoginModal={() => setShowLoginModal(true)}
        setShowSettingsModal={() => setShowSettingsModal(true)}
        handleRemoveGraph={handleRemoveGraph}
        handleRebuildGraph={handleRebuildGraph}
        handleRandomNodeSelection={handleRandomNodeSelection}
        sendNewPrompt={sendNewPrompt}
        newPrompt={newPrompt}
        setNewPrompt={setNewPrompt}
        isNodeListOpen={isNodeListOpen}
        handleNodeListToggle={() => setIsNodeListOpen(!isNodeListOpen)}
        handleZoomOut={handleZoomOut}
      />

      <NotificationContainer>
        {contextHolder}
      </NotificationContainer>

      <div
        ref={graphRef}
        onDoubleClick={handleDoubleClick}
        style={{ width: "100%", height: "100%", position: "relative" }}
      >
        <Graph
          getNetwork={(network) => { networkRef.current = network; }}
          options={{ ...constants.graph.options, ...{ height: `${screenHeight}px`, width: `${screenWidth}px` } }}
          events={events}
        />
        {graph && <GraphLegend legend={graph.legend} sidebarOpen={isNodeListOpen} />}
      </div>



      <NodeListSidebar
        graph={graph}
        networkRef={networkRef}
        isOpen={isNodeListOpen}
        onToggle={() => setIsNodeListOpen(!isNodeListOpen)}
        onNodeSelect={(node) => {
          setLastSelectedNode(node);
        }}
        onNodeChat={(node) => {
          setLastSelectedNode(node);
          // Find parent nodes for the selected node
          const parentNodes = findParents(graph, node.id).filter(n => n !== undefined);
          setLastSelectedNodeParents(parentNodes);
          setShowNodeContent(true);
        }}
      />

      {showChat && (
        <Modal title={t('nodeContent.chatAbout')}>
          <Chat
            key={`chat-${lastSelectedNode.id}`}
            topic={lastSelectedNodeParents && lastSelectedNodeParents.length > 0
              ? lastSelectedNodeParents.map(p => p.label).join(', ')
              : graph.topic || ''}
            subject={`${lastSelectedNode.label}`}
            notice={notice}
            setShowLoginModal={setShowLoginModal}
            fileId={fileId}
            initialChatId={lastSelectedNode.chatId}
            onChatIdCreated={(chatId) => handleChatIdCreated(lastSelectedNode.id, chatId)}
            onClose={() => setShowNodeContent(false)}
          />
        </Modal>
      )}


      {showEdit && (
        <Modal>
          <NodeContent
            node={lastSelectedNode}
            parentNode={lastSelectedNodeParents[0] || null}
            graph={graph}
            setGraph={setGraph as (g: any) => void}
            notice={notice}
            setShowLoginModal={setShowLoginModal}
            setShowNodeContent={setShowEdit}
            setShowChat={setShowNodeContent}
          />
        </Modal>
      )}

      {showLoginModal && (
        <LoginModal notification={notice} topic={topic} />
      )}

      {showSettingsModal && (
        <SettingsModal />
      )}

      {flashcardNode && (
        <FlashcardModal
          topic={isFileMode ? '' : (graph.topic || topic || '')}
          subtopic={flashcardNode.label}
          fileId={isFileMode ? fileId : null}
          neighbors={flashcardNeighbors}
          cachedCards={flashcardNode.flashcards || null}
          cachedRevealed={flashcardNode.flashcardsRevealed || null}
          onCardsGenerated={(cards: any[]) => {
            setGraph(prev => {
              const updatedNodes = prev.nodes.map((n: any) =>
                n.id === flashcardNode.id ? { ...n, flashcards: cards } : n
              );
              const updatedGraph = { ...prev, nodes: updatedNodes };
              saveGraph(updatedGraph, topic || fileId);
              return updatedGraph;
            });
          }}
          onReveal={(revealed: number[]) => {
            setGraph(prev => {
              const updatedNodes = prev.nodes.map((n: any) =>
                n.id === flashcardNode.id ? { ...n, flashcardsRevealed: revealed } : n
              );
              const updatedGraph = { ...prev, nodes: updatedNodes };
              saveGraph(updatedGraph, topic || fileId);
              return updatedGraph;
            });
          }}
          onCompleted={() => {
            setGraph(prev => {
              const updatedNodes = prev.nodes.map((n: any) =>
                n.id === flashcardNode.id ? { ...n, flashcardsCompleted: true } : n
              );
              const updatedGraph = { ...prev, nodes: updatedNodes };
              saveGraph(updatedGraph, topic || fileId);
              return updatedGraph;
            });
          }}
        />
      )}

      <Menu id={menuId}>
        {showChatAboutContextButton ?
          <Item onClick={async () => {
            const node = graph.nodes.find((n: any) => n.id === lastHoveredNode);
            if (node) {
              setShowContextMenu(false);
              hideAll();
              // Build neighbor context: incoming and outgoing edges with labels
              const { nodes: neighborNodes, edges: neighborEdges } = getNeighborsGraphById(node.id, graph);
              const incoming = neighborEdges
                .filter((e: any) => e.to === node.id)
                .map((e: any) => {
                  const src = neighborNodes.find((n: any) => n.id === e.from);
                  return { label: src?.label, relationship: e.label };
                })
                .filter((n: any) => n.label);
              const outgoing = neighborEdges
                .filter((e: any) => e.from === node.id)
                .map((e: any) => {
                  const tgt = neighborNodes.find((n: any) => n.id === e.to);
                  return { label: tgt?.label, relationship: e.label };
                })
                .filter((n: any) => n.label);
              setFlashcardNeighbors({ incoming, outgoing });
              setFlashcardNode(node);
            }
          }}>{t('flashcards.title', 'Flashcards')}</Item>
          : null}

        {showChatAboutContextButton ?
          <Item onClick={handleBuildGraphInNewTab}>{t('graph.buildGraph')}</Item>
          : null}

        {showChatAboutContextButton ?
          <SubmenuSearchAbout getSearchQuery={getSearchQuery} />
          : null}

        {/* Expand/Collapse hidden for now — feature needs rework
        {showChatAboutContextButton && lastHoveredNode && graph.edges.some(e => e.from === lastHoveredNode) && !expandedNodes.has(lastHoveredNode) ?
          <Item onClick={() => {
            setExpandedNodes(prev => new Set([...prev, lastHoveredNode]));
            setShowContextMenu(false);
            hideAll();
          }}>{t('graph.expand')}</Item>
          : null}

        {showChatAboutContextButton && lastHoveredNode && expandedNodes.has(lastHoveredNode) ?
          <Item onClick={() => {
            setExpandedNodes(prev => {
              const next = new Set(prev);
              next.delete(lastHoveredNode);
              return next;
            });
            setShowContextMenu(false);
            hideAll();
          }}>{t('graph.collapse')}</Item>
          : null}
        */}
      </Menu>

    </div>
  );
};

export default GameGraph;
